import React from "react";

import { DefaultValues, FormProvider, useForm, UseFormReturn } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { WizardContext } from "@/components/organisms";
import { useScrollToError } from "@/shared/hooks";

import { WizardProps } from "../Wizard";

export type WizardFormProviderProps<FormType> = {
    formContext: WizardContext;
    formDefaultValues: FormType;
    prepareDataForValidation?: (values: FormType) => FormType;
    children: (formMethods: UseFormReturn<FormType>) => React.ReactElement<WizardProps>;
};

const useWizardFormProvider = <FormType,>(
    wizardContext: WizardContext,
    defaultValues: FormType,
    prepareDataForValidation: (values: FormType) => FormType = null,
): UseFormReturn<FormType> => {
    return useForm<FormType>({
        mode: "onBlur",
        defaultValues: defaultValues as DefaultValues<FormType>, // DefaultValues<FormType> = Partial<FormType>
        resolver: (data, context: WizardContext, options) => {
            const step = context.wizardStep;
            const dataToValidate = prepareDataForValidation ? prepareDataForValidation(data) : data;
            return yupResolver(step.schema || yup.object())(dataToValidate, context, options);
        },

        context: wizardContext,
    });
};

const WizardFormProvider = <FormType,>({
    formContext,
    formDefaultValues,
    prepareDataForValidation,
    children,
}: WizardFormProviderProps<FormType>): React.ReactElement => {
    const formMethods = useWizardFormProvider(formContext, formDefaultValues, prepareDataForValidation);
    const {
        formState: { errors },
    } = formMethods;

    useScrollToError(errors);

    return <FormProvider {...formMethods}>{children(formMethods)}</FormProvider>;
};

export default WizardFormProvider;
